import {
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { PersonSearchCapability, type PersonDto } from "../../autogen/bff-api";
import { useApiError } from "../errors/useApiError";
import { PersonSelector } from "./PersonSelector";

const personName = (p: PersonDto) => [p.firstName, p.lastName].filter(Boolean).join(" ") || p.email;

export const ChangeResponsibleButton = ({
  ownerOrgId,
  departmentId,
  currentResponsible,
  explanation,
  save,
  isSaving,
  describeFailure = () => null,
  variant = "link",
  title,
  savedMessage,
  capability = PersonSearchCapability.ManageSourcingEvent,
  ariaLabel,
}: {
  ownerOrgId: string;
  departmentId?: string | null;
  currentResponsible: PersonDto | null;
  explanation: string;
  save: (personId: string) => Promise<void>;
  isSaving: boolean;
  describeFailure?: (error: unknown) => string | null;
  variant?: "link" | "outline";
  title?: string;
  savedMessage?: string;
  capability?: PersonSearchCapability | null;
  ariaLabel?: string;
}) => {
  const { t } = useTranslation();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();
  const { trigger } = useApiError();
  const [selected, setSelected] = useState<PersonDto | null>(currentResponsible);
  const [failure, setFailure] = useState<string | null>(null);

  const changed = !!selected && selected.id !== currentResponsible?.id;
  const heading = title ?? t("Change responsible buyer");
  const eligibility = capability ? { capability, departmentId: departmentId ?? null } : {};

  const open = () => {
    setSelected(currentResponsible);
    setFailure(null);
    onOpen();
  };

  const onConfirm = async () => {
    if (!changed || !selected) return;
    setFailure(null);
    try {
      await save(selected.id);
      toast({ title: savedMessage ?? t("Responsible buyer changed"), status: "success", duration: 4000, isClosable: true });
      onClose();
    } catch (e) {
      const described = describeFailure(e);
      if (described) setFailure(described);
      else trigger(e);
    }
  };

  return (
    <>
      <Button variant={variant === "link" ? "link" : "outline"} size="sm" color="smPrimary" onClick={open} aria-label={ariaLabel}>
        {t("Change")}
      </Button>
      <Modal isOpen={isOpen} onClose={onClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{heading}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Stack spacing="4">
              <Text fontSize="sm" color="smMuted">
                {explanation}
              </Text>
              <PersonSelector
                size="md"
                organizationIds={[ownerOrgId]}
                {...eligibility}
                selectedPerson={selected}
                onChange={setSelected}
                isClearable={false}
              />
              {changed && selected && (
                <Text fontSize="sm" color="smText">
                  {t("New responsible: {{name}}", { name: personName(selected) })}
                </Text>
              )}
              {failure && (
                <Text fontSize="sm" color="smError">
                  {failure}
                </Text>
              )}
            </Stack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr="3" onClick={onClose} isDisabled={isSaving}>
              {t("Cancel")}
            </Button>
            <Button colorScheme="teal" onClick={() => void onConfirm()} isLoading={isSaving} isDisabled={!changed}>
              {heading}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};
